import { useState } from 'react';
import {
  Box,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import CheckIcon from '@mui/icons-material/Check';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import { SCROLL_MARGIN_TOP } from './OnThisPage';
import { deriveFamily } from './catalogGrouping';
import { docs } from './docsStyles';
import { generateUsageSnippet } from './sandpackMapper';

const packageShortName = pkg => (pkg.split('/').pop() || pkg).toUpperCase();

// A titled, anchorable section. The `id` matches the OnThisPage table-of-contents
// anchors; scrollMarginTop keeps the heading clear of the sticky app header.
function Section({ id, title, children }) {
  return (
    <Box id={id} sx={{ scrollMarginTop: `${SCROLL_MARGIN_TOP}px`, mb: 4 }}>
      <Typography
        sx={{
          fontFamily: docs.font,
          fontSize: 12,
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: 0.8,
          color: docs.textFaint,
          mb: 1.25,
        }}>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

// Small uppercase header cell for the props table.
function HeadCell({ children }) {
  return (
    <TableCell
      sx={{
        fontFamily: docs.font,
        fontSize: 11,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: 0.6,
        color: docs.textFaint,
        borderColor: docs.border,
        py: 1,
      }}>
      {children}
    </TableCell>
  );
}

// Presentational detail for a single catalog component: breadcrumb + title + metadata,
// a terminal-style import block with copy, then anchored Description / Props / Dependencies.
// Degrades gracefully when docs are incomplete (plan T3.3 / T3.5).
function ComponentDetail({ component, t, playgroundSlot }) {
  const [copied, setCopied] = useState(false);
  const [copiedUsage, setCopiedUsage] = useState(false);
  const props = Array.isArray(component.props) ? component.props : [];
  const dependencyEntries = Object.entries(component.dependencies || {});
  const family = deriveFamily(component);
  const importLine = `import { ${component.name} } from '${component.package}';`;
  const usageSnippet = generateUsageSnippet(component);

  const copyImport = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(importLine);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };

  const copyUsage = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(usageSnippet);
      setCopiedUsage(true);
      setTimeout(() => setCopiedUsage(false), 1500);
    }
  };

  return (
    <Box sx={{ fontFamily: docs.font }}>
      {/* Breadcrumb */}
      <Typography sx={{ fontFamily: docs.font, fontSize: 12, color: docs.textFaint, mb: 1 }}>
        {packageShortName(component.package)}
        <Box component="span" sx={{ mx: 0.75 }}>
          /
        </Box>
        {family.label}
        <Box component="span" sx={{ mx: 0.75 }}>
          /
        </Box>
        <Box component="span" sx={{ color: docs.textMuted }}>
          {component.name}
        </Box>
      </Typography>

      {/* Title */}
      <Typography
        sx={{ fontFamily: docs.font, fontWeight: 700, fontSize: 30, color: docs.textStrong, mb: 1.25 }}>
        {component.name}
      </Typography>

      {/* Metadata chips */}
      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1, mb: 2.5 }}>
        <MetaChip label={component.package} />
        <MetaChip label={family.label} />
        {component.sandboxType && (
          <MetaChip
            label={t('internalLibraries.sandbox_type', { defaultValue: 'Sandbox: {{type}}', type: component.sandboxType })}
            dot={docs.ready}
          />
        )}
      </Stack>

      {/* Terminal-style import block */}
      <Box
        sx={{
          position: 'relative',
          bgcolor: docs.codeBg,
          borderRadius: 2,
          px: 2,
          py: 1.5,
          mb: 4,
        }}>
        <Box
          component="code"
          sx={{
            fontFamily: docs.monoFont,
            fontSize: 13,
            color: docs.codeText,
            whiteSpace: 'pre',
            display: 'block',
            overflowX: 'auto',
            pr: 4,
          }}>
          {importLine}
        </Box>
        <Tooltip title={copied ? t('internalLibraries.copied', 'Copied') : t('internalLibraries.copy', 'Copy')}>
          <IconButton
            size="small"
            onClick={copyImport}
            sx={{ position: 'absolute', top: 8, right: 8, color: copied ? docs.ready : '#94a3b8' }}>
            {copied ? (
              <CheckIcon sx={{ fontSize: 16 }} />
            ) : (
              <ContentCopyOutlinedIcon sx={{ fontSize: 16 }} />
            )}
          </IconButton>
        </Tooltip>
      </Box>

      <Section id="description" title={t('internalLibraries.description_title', 'Description')}>
        <Typography sx={{ fontFamily: docs.font, fontSize: 14, lineHeight: 1.6, color: docs.text }}>
          {component.description || t('internalLibraries.no_description', 'No description available.')}
        </Typography>
      </Section>

      {playgroundSlot}

      {/* Usage skeleton — copy-paste starter for the developer */}
      <Box id="usage" sx={{ scrollMarginTop: `${SCROLL_MARGIN_TOP}px`, mb: 4 }}>
        <Typography
          sx={{
            fontFamily: docs.font,
            fontSize: 12,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: 0.8,
            color: docs.textFaint,
            mb: 1.25,
          }}>
          {t('internalLibraries.usage_title', 'Usage')}
        </Typography>
        <Box
          sx={{
            position: 'relative',
            bgcolor: docs.codeBg,
            borderRadius: 2,
            px: 2,
            py: 1.75,
          }}>
          <Box
            component="pre"
            sx={{
              fontFamily: docs.monoFont,
              fontSize: 13,
              lineHeight: 1.65,
              color: docs.codeText,
              margin: 0,
              overflowX: 'auto',
              pr: 4,
              whiteSpace: 'pre',
            }}>
            {usageSnippet}
          </Box>
          <Tooltip title={copiedUsage ? t('internalLibraries.copied', 'Copied') : t('internalLibraries.copy', 'Copy')}>
            <IconButton
              size="small"
              onClick={copyUsage}
              sx={{ position: 'absolute', top: 8, right: 8, color: copiedUsage ? docs.ready : '#94a3b8' }}>
              {copiedUsage ? (
                <CheckIcon sx={{ fontSize: 16 }} />
              ) : (
                <ContentCopyOutlinedIcon sx={{ fontSize: 16 }} />
              )}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Section id="props" title={t('internalLibraries.props_title', 'Props')}>
        {props.length > 0 ? (
          <Box sx={{ border: '1px solid', borderColor: docs.border, borderRadius: 2, overflow: 'hidden' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: docs.bgSubtle }}>
                  <HeadCell>{t('internalLibraries.prop_name', 'Name')}</HeadCell>
                  <HeadCell>{t('internalLibraries.prop_type', 'Type')}</HeadCell>
                  <HeadCell>{t('internalLibraries.prop_default', 'Default')}</HeadCell>
                  <HeadCell>{t('internalLibraries.prop_description', 'Description')}</HeadCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {props.map(prop => (
                  <TableRow key={prop.name}>
                    <TableCell sx={{ borderColor: docs.border, py: 1.1, verticalAlign: 'top' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          component="code"
                          sx={{
                            fontFamily: docs.monoFont,
                            fontSize: 13,
                            fontWeight: 600,
                            color: docs.textStrong,
                          }}>
                          {prop.name}
                        </Box>
                        {prop.required && (
                          <Box
                            component="span"
                            sx={{
                              fontFamily: docs.font,
                              fontSize: 10,
                              fontWeight: 700,
                              letterSpacing: 0.5,
                              color: docs.reqText,
                              bgcolor: docs.reqBg,
                              borderRadius: 1,
                              px: 0.6,
                              py: '1px',
                            }}>
                            {t('internalLibraries.required_badge', 'REQ')}
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ borderColor: docs.border, py: 1.1, verticalAlign: 'top' }}>
                      {prop.type ? <TypeChip type={prop.type} /> : <Faint />}
                    </TableCell>
                    <TableCell sx={{ borderColor: docs.border, py: 1.1, verticalAlign: 'top' }}>
                      {prop.defaultValue !== undefined && prop.defaultValue !== '' ? (
                        <Box
                          component="code"
                          sx={{
                            fontFamily: docs.monoFont,
                            fontSize: 12.5,
                            color: docs.textMuted,
                          }}>
                          {String(prop.defaultValue)}
                        </Box>
                      ) : (
                        <Faint />
                      )}
                    </TableCell>
                    <TableCell sx={{ borderColor: docs.border, py: 1.1, verticalAlign: 'top', maxWidth: 280 }}>
                      {prop.description ? (
                        <Box
                          sx={{
                            maxHeight: 80,
                            overflowY: 'auto',
                            fontFamily: docs.font,
                            fontSize: 13,
                            lineHeight: 1.5,
                            color: docs.textMuted,
                            pr: 0.5,
                            '&::-webkit-scrollbar': { width: 4 },
                            '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
                            '&::-webkit-scrollbar-thumb': { bgcolor: docs.borderStrong, borderRadius: 2 },
                          }}>
                          {prop.description}
                        </Box>
                      ) : (
                        <Faint />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        ) : (
          <Typography sx={{ fontFamily: docs.font, fontSize: 14, color: docs.textMuted }}>
            {t('internalLibraries.no_props', 'No documented props.')}
          </Typography>
        )}
      </Section>

      <Section id="dependencies" title={t('internalLibraries.dependencies_title', 'Dependencies')}>
        {dependencyEntries.length > 0 ? (
          <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1 }}>
            {dependencyEntries.map(([name, version]) => (
              <Box
                key={name}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  fontFamily: docs.font,
                  fontSize: 12.5,
                  color: docs.text,
                  bgcolor: docs.chipBg,
                  border: '1px solid',
                  borderColor: docs.chipBorder,
                  borderRadius: 1.5,
                  px: 1,
                  py: 0.4,
                }}>
                <Inventory2OutlinedIcon sx={{ fontSize: 14, color: docs.textFaint }} />
                {name}
                <Box component="span" sx={{ color: docs.textFaint }}>
                  {version}
                </Box>
              </Box>
            ))}
          </Stack>
        ) : (
          <Typography sx={{ fontFamily: docs.font, fontSize: 14, color: docs.textMuted }}>
            {t('internalLibraries.no_dependencies', 'No external dependencies.')}
          </Typography>
        )}
      </Section>
    </Box>
  );
}

// Outlined metadata chip; an optional colored dot marks the live sandbox.
function MetaChip({ label, dot }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.6,
        fontFamily: docs.font,
        fontSize: 12,
        color: docs.textMuted,
        bgcolor: docs.bg,
        border: '1px solid',
        borderColor: docs.chipBorder,
        borderRadius: 1.5,
        px: 1,
        py: 0.35,
      }}>
      {dot && (
        <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: dot, flexShrink: 0 }} />
      )}
      {label}
    </Box>
  );
}

// Monospace type chip used in the props table.
function TypeChip({ type }) {
  return (
    <Box
      component="code"
      sx={{
        fontFamily: docs.monoFont,
        fontSize: 12,
        color: docs.typeChipText,
        bgcolor: docs.typeChipBg,
        borderRadius: 1,
        px: 0.75,
        py: '2px',
      }}>
      {type}
    </Box>
  );
}

function Faint() {
  return <Box component="span" sx={{ color: docs.textFaint }}>—</Box>;
}

export default ComponentDetail;
